import {ISimplePresentationRunnerController} from "../../core/winpresentation/SimplePresentationRunner";
import {KennethMustDie} from "../KennethMustDie";
import {IBetWayWinData, SPECIAL_SYMBOL_NAMES} from "../server/IKMDData";
import {KMDGameMode} from "../server/KMDGameMode";
import {SimpleReelModel, SimpleReelModelSymbolData} from "../../core/reel/simplereel/SimpleReelModel";
import {KMDSymbol} from "../reels/KMDSymbol";
import {gsap} from "gsap";
import {KMDSound} from "../sound/KMDSound";
import {KMDNormalBetWaysWinRunner} from "./KMDNormalBetWaysWinRunner";

export class KMDWinRunner extends KMDNormalBetWaysWinRunner {

    private _lastReelForSound: number;

    public shouldRun(): boolean {
        return (KennethMustDie.model.gameData.betWayWins.length > 0 || KennethMustDie.model.specialBetWayWins.length > 0);
    }

    public start(controller: ISimplePresentationRunnerController): void {
        // #236
        this._lastReelForSound = -1;

        this._canPlayWinSounds = !KennethMustDie.isSingleWinBelowStake();

        const nextMode: string = KennethMustDie.model.data.nextMode,
            useTotalWin: boolean = (nextMode !== KMDGameMode.NORMAL || KennethMustDie.model.gameData.mode !== KMDGameMode.NORMAL),
            normalBetWayWins: IBetWayWinData[] = KennethMustDie.model.gameData.betWayWins,
            betWayWins: IBetWayWinData[] = KennethMustDie.model.specialBetWayWins,
            tl: gsap.core.Timeline = gsap.timeline({
                delay: 0.1,
                onComplete: () => {
                    controller.finish();
                }
            }),
            allWinPositionsSpecialBetWayWins: boolean[][] = this.getAllWinPositions(betWayWins),
            allWinPositions: boolean[][] = this.getAllWinPositionsFinal(normalBetWayWins, betWayWins),
            evalArea: string[][] = KennethMustDie.model.gameData.evaluatedArea,
            m1M2SymbolLines: number[] = KennethMustDie.model.gameData.symbolLines;

        // #326
        KennethMustDie.model.multipliersForReelStop = JSON.parse(JSON.stringify(KennethMustDie.model.gameData.multiplier));

        tl.add(() => {
            controller.setSkipCallback(() => {
                this.onSkip(tl);
            });

            // making sure, all win symbols do not look like noWin, because after m1m2 spark feature there can be situations in which a noWin sym can turn to win sym.
            if (m1M2SymbolLines && m1M2SymbolLines.length) {
                this.sendSymbolsToNormal(allWinPositions, evalArea);
            }
        });

        // if m1m2 feature triggered after KMDNormalBetWaysWinRunner, then we shall scan reel area again for final noWin.
        if (m1M2SymbolLines && m1M2SymbolLines.length) {
            this.animateNoWin(allWinPositions, tl);
        }

        if (betWayWins.length) {
            this.animatePreWin(betWayWins, tl);
            this.animateWin(allWinPositionsSpecialBetWayWins, tl);
        }

        // #289
        const isM1M2FeatureActivated: boolean = (!KennethMustDie.model.specialBetWayWins.length && m1M2SymbolLines && m1M2SymbolLines.length > 0);
        if (isM1M2FeatureActivated) {
            this.handleM1M2SparkFeature(m1M2SymbolLines, allWinPositions, tl, evalArea);
        }

        // #343
        tl.addLabel("doIgnition");
        if (betWayWins.length) {
            this.showWinAmountNWays(betWayWins, tl, "doIgnition");
        } else if (!isM1M2FeatureActivated) {
            this.showWinAmountNWays(normalBetWayWins, tl, "doIgnition");
        }

        if (nextMode.indexOf("AVALANCHE") > -1) {
            if (KennethMustDie.model.winRatio < 5) {
                this.igniteForLowerWins(allWinPositions, tl);
                this.explodeForLowerWins(allWinPositions, tl);
            } else {
                if (KennethMustDie.model.winRatio < KennethMustDie.model.bigWinLevelRatioStarter) {
                    this.igniteForHigherWins(allWinPositions, tl);
                } else {
                    this.igniteForBigWins(betWayWins, tl, normalBetWayWins);
                }
                this.explodeForHigherWins(allWinPositions, tl);
            }
        } else if (KennethMustDie.model.winRatio >= KennethMustDie.model.bigWinLevelRatioStarter) {
            this.igniteForBigWins(betWayWins, tl, normalBetWayWins);
            this.turnOffIgnitionForBigWins(allWinPositions, tl);
        }

        if (!KennethMustDie.model.gameData.brokeTheBank && nextMode.indexOf("AVALANCHE") > -1) {
            tl.add(() => {
                KennethMustDie.setKeypadWin(useTotalWin);
            });
        }

        if (nextMode.indexOf("AVALANCHE") > -1) {
            this.doCollapseAction(tl, allWinPositions);

            // #326
            allWinPositions.forEach((values: boolean[], reelId: number) => {
                // should only modify main reels
                for (let symId: number = 0; symId < values.length - 1; symId++) {
                    if (values[symId] && !KennethMustDie.model.reSpawnPositions[reelId][symId]) {
                        KennethMustDie.model.multipliersForReelStop[reelId][symId] = 0;
                    }
                }
            });
        }
    }

    private getAllWinPositionsFinal(betWayWins: IBetWayWinData[], specialBetWayWins: IBetWayWinData[]): boolean[][] {
        const allWinPositions: boolean[][] = [
            [false, false, false, false],
            [false, false, false, false],
            [false, false, false, false],
            [false, false, false, false],
            [false, false, false, false]
        ];

        betWayWins.forEach((betWayWin: IBetWayWinData) => {
            betWayWin.reelWinPositions.forEach((value: boolean[], reelId: number) => {
                value.forEach((val: boolean, symId: number) => {
                    if (val) {
                        allWinPositions[reelId][symId] = val;

                        // #236
                        if (!KennethMustDie.model.reSpawnPositions[reelId][symId]) {
                            this._lastReelForSound = reelId;
                        }
                    }
                });
            });
        });

        specialBetWayWins.forEach((betWayWin: IBetWayWinData) => {
            betWayWin.reelWinPositions.forEach((value: boolean[], reelId: number) => {
                value.forEach((val: boolean, symId: number) => {
                    if (val) {
                        allWinPositions[reelId][symId] = val;

                        // #236
                        if (!KennethMustDie.model.reSpawnPositions[reelId][symId]) {
                            this._lastReelForSound = reelId;
                        }
                    }
                });
            });
        });

        return allWinPositions;
    }

    private sendSymbolsToNormal(allWinPositions: boolean[][], evalArea: string[][]): void {
        allWinPositions.forEach((winPositions: boolean[], reelId: number) => {
            winPositions.forEach((val: boolean, symId: number) => {
                if (val && evalArea[reelId][symId] !== SPECIAL_SYMBOL_NAMES.NW) { // if a position is NudgeWild in eval area, no need to change it, as NW can't be on noWin state
                    const reel: SimpleReelModel = (symId === 3 && reelId > 0 && reelId < 4) ? KennethMustDie.specialReelView.reel : KennethMustDie.reelView.reelModels[reelId],
                        effectiveSymId: number = (reelId > 0 && reelId < 4 ? (reel.id === 5 ? (reelId - 1) : symId) : (symId - 1)),
                        symbol: SimpleReelModelSymbolData = reel.getStopSymbolAt(effectiveSymId),
                        sym: KMDSymbol = symbol.positionable as KMDSymbol;

                    if (sym.getExtrasLength()) {
                        KennethMustDie.xWaysReveal.removeNoWinTint(reelId);
                    }

                    sym.setToNormalState();
                }
            });
        });
    }

    private handleM1M2SparkFeature(m1M2SymbolLines: number[], allWinPositions: boolean[][], timeLine: gsap.core.Timeline, evalArea: string[][]): void {
        timeLine.add(() => {
            allWinPositions.forEach((value: boolean[], reelId: number) => {
                value.forEach((val: boolean, symId: number) => {
                    if (val && evalArea[reelId][symId] !== SPECIAL_SYMBOL_NAMES.NW) { // #349, if a position is NudgeWild in eval area, no need to change it
                        const reel: SimpleReelModel = (symId === 3 && reelId > 0 && reelId < 4) ? KennethMustDie.specialReelView.reel : KennethMustDie.reelView.reelModels[reelId],
                            effectiveSymId: number = (reelId > 0 && reelId < 4 ? (reel.id === 5 ? (reelId - 1) : symId) : (symId - 1)),
                            symbol: SimpleReelModelSymbolData = reel.getStopSymbolAt(effectiveSymId),
                            sym: KMDSymbol = symbol.positionable as KMDSymbol,
                            multi: number = KennethMustDie.model.gameData.multiplier[reelId][symId];

                        if (m1M2SymbolLines.indexOf((reelId * 4) + symId) > -1) {
                            let symName: string = KennethMustDie.reelAreaSymbolsName[reelId][symId];
                            if (symName === SPECIAL_SYMBOL_NAMES.X_WAYS) {
                                symName = KennethMustDie.model.gameData.mysterySymbolRevealed;
                            }

                            sym.updateTexture(symName, multi);
                        }
                    }
                });
            });
        });
        timeLine.to(this, {
            duration: 0.1
        });
    }

    private igniteForLowerWins(allWinPositions: boolean[][], tl: gsap.core.Timeline): void {
        const timeLine: gsap.core.Timeline = gsap.timeline(),
            visitedIdx: number[] = [],
            ts: number = 1.5;
        let specialRowSoundPlayed: boolean = false,
            mainReelsSoundPlayed: boolean = false;

        for (let reelId: number = 1; reelId < allWinPositions.length - 1; reelId++) {
            if (allWinPositions[reelId][3]) {
                const effectiveSymId: number = (reelId - 1),
                    symbol: SimpleReelModelSymbolData = KennethMustDie.specialReelView.reel.getStopSymbolAt(effectiveSymId),
                    sym: KMDSymbol = symbol.positionable as KMDSymbol,
                    idx: number = (reelId * 4) + 3;

                if (visitedIdx.indexOf(idx) === -1) {
                    visitedIdx.push(idx);
                    timeLine.add(sym.playIgniteOverlayAnimation(reelId).timeScale(ts), 0);

                    // #411
                    if (this._canPlayWinSounds && !specialRowSoundPlayed) {
                        specialRowSoundPlayed = true;
                        timeLine.add(() => {
                            KennethMustDie.soundPlayer.playSound(KMDSound.WIN_SET_SYMBOL_ON_FIRE);
                        }, 0);
                    }
                }
            }
        }
        timeLine.addLabel("specialReelIgnited");

        allWinPositions.forEach((value: boolean[], reelId: number) => {
            for (let symId: number = 0; symId < value.length - 1; symId++) {
                if (allWinPositions[reelId][symId]) {
                    const effectiveSymId: number = (reelId > 0 && reelId < 4 ? symId : (symId - 1)),
                        symbol: SimpleReelModelSymbolData = KennethMustDie.reelView.reelModels[reelId].getStopSymbolAt(effectiveSymId),
                        sym: KMDSymbol = symbol.positionable as KMDSymbol,
                        idx: number = (reelId * 4) + symId;

                    if (visitedIdx.indexOf(idx) === -1) {
                        visitedIdx.push(idx);
                        timeLine.add(sym.playIgniteOverlayAnimation(reelId).timeScale(ts), "specialReelIgnited");

                        // #411
                        if (this._canPlayWinSounds && !mainReelsSoundPlayed) {
                            mainReelsSoundPlayed = true;
                            timeLine.add(() => {
                                KennethMustDie.soundPlayer.playSound(KMDSound.WIN_SET_SYMBOL_ON_FIRE);
                            }, "specialReelIgnited");
                        }
                    }
                }
            }
        });

        tl.add(timeLine, "doIgnition");
    }

    private explodeForLowerWins(allWinPositions: boolean[][], tl: gsap.core.Timeline): void {
        const timeLine: gsap.core.Timeline = gsap.timeline();

        // #236
        timeLine.add(() => {
            if (this._canPlayWinSounds) {
                KennethMustDie.soundPlayer.playSound(KMDSound.SYM_EXPLODE_1);
            }

            for (let reelId: number = 0; reelId < KennethMustDie.REELS_NUM; reelId++) {
                KennethMustDie.winOverlays.stopIgniteAnimation(reelId);
            }
        }, (11 / 30));

        allWinPositions.forEach((value: boolean[], reelId: number) => {
            value.forEach((val: boolean, symId: number) => {
                if (val) {
                    const reel: SimpleReelModel = (symId === 3 && reelId > 0 && reelId < 4) ? KennethMustDie.specialReelView.reel : KennethMustDie.reelView.reelModels[reelId],
                        effectiveSymId: number = (reelId > 0 && reelId < 4 ? (reel.id === 5 ? (reelId - 1) : symId) : (symId - 1)),
                        symbol: SimpleReelModelSymbolData = reel.getStopSymbolAt(effectiveSymId),
                        sym: KMDSymbol = symbol.positionable as KMDSymbol,
                        multi: number = KennethMustDie.model.gameData.multiplier[reelId][symId];

                    if (!KennethMustDie.model.reSpawnPositions[reelId][symId]) {
                        timeLine.add([
                            sym.playOverlayAnimation("symExplode"),
                            sym.displayMultiOnWin(multi)
                        ], 0);

                        timeLine.add(() => {
                            reel.removeAvalancheSymbol(symbol);

                            if (sym.getExtrasLength()) {
                                KennethMustDie.xWaysReveal.hideItem(reelId);
                            }

                            // #245
                            if (reel.id === KennethMustDie.REELS_NUM) {
                                KennethMustDie.multipliersChain.removeMultiplier(effectiveSymId);
                            }
                        }, "-=0.5");
                    }
                }
            });
        });

        tl.add(timeLine);
    }

    private igniteForHigherWins(allWinPositions: boolean[][], tl: gsap.core.Timeline): void {
        const timeLine: gsap.core.Timeline = gsap.timeline(),
            visitedIdx: number[] = [],
            ts: number = 1.5;
        let specialRowSoundPlayed: boolean = false;

        for (let reelId: number = 1; reelId < allWinPositions.length - 1; reelId++) {
            if (allWinPositions[reelId][3]) {
                const effectiveSymId: number = (reelId - 1),
                    symbol: SimpleReelModelSymbolData = KennethMustDie.specialReelView.reel.getStopSymbolAt(effectiveSymId),
                    sym: KMDSymbol = symbol.positionable as KMDSymbol,
                    idx: number = (reelId * 4) + 3;

                if (visitedIdx.indexOf(idx) === -1) {
                    visitedIdx.push(idx);
                    timeLine.add(sym.playIgniteOverlayAnimation(reelId).timeScale(ts), 0);

                    // #411
                    if (this._canPlayWinSounds && !specialRowSoundPlayed) {
                        specialRowSoundPlayed = true;
                        timeLine.add(() => {
                            KennethMustDie.soundPlayer.playSound(KMDSound.WIN_SET_SYMBOL_ON_FIRE);
                        }, 0);
                    }
                }
            }
        }

        const reelTl: gsap.core.Timeline = gsap.timeline();
        allWinPositions.forEach((value: boolean[], reelId: number) => {
            const symTl: gsap.core.Timeline = gsap.timeline();
            let perReelSoundPlayed: boolean = false;

            for (let symId: number = 0; symId < value.length - 1; symId++) {
                if (allWinPositions[reelId][symId]) {
                    const effectiveSymId: number = (reelId > 0 && reelId < 4 ? symId : (symId - 1)),
                        symbol: SimpleReelModelSymbolData = KennethMustDie.reelView.reelModels[reelId].getStopSymbolAt(effectiveSymId),
                        sym: KMDSymbol = symbol.positionable as KMDSymbol,
                        idx: number = (reelId * 4) + symId;

                    if (visitedIdx.indexOf(idx) === -1) {
                        visitedIdx.push(idx);
                        symTl.add(sym.playIgniteOverlayAnimation(reelId).timeScale(ts + (reelId * 0.3)), 0);

                        // #411
                        if (this._canPlayWinSounds && !perReelSoundPlayed) {
                            perReelSoundPlayed = true;
                            symTl.add(() => {
                                KennethMustDie.soundPlayer.playSound(KMDSound.WIN_SET_SYMBOL_ON_FIRE);
                            }, 0);
                        }
                    }
                }
            }
            reelTl.add(symTl);
        });
        timeLine.add(reelTl);

        tl.add(timeLine, "doIgnition");
    }

    private igniteForBigWins(betWayWins: IBetWayWinData[], tl: gsap.core.Timeline, normalBetWayWins: IBetWayWinData[]): void {
        betWayWins = betWayWins.concat(normalBetWayWins);

        const timeLine: gsap.core.Timeline = gsap.timeline(),
            visitedIdx: number[] = [],
            initTs: number = 1.5;

        betWayWins.forEach((betWayWin: IBetWayWinData, betWayIdx: number) => {
            const specialReelTl: gsap.core.Timeline = gsap.timeline();
            let specialRowSoundPlayed: boolean = false;

            for (let reelId: number = 1; reelId < betWayWin.reelWinPositions.length - 1; reelId++) {
                if (betWayWin.reelWinPositions[reelId][3]) {
                    const effectiveSymId: number = (reelId - 1),
                        symbol: SimpleReelModelSymbolData = KennethMustDie.specialReelView.reel.getStopSymbolAt(effectiveSymId),
                        sym: KMDSymbol = symbol.positionable as KMDSymbol,
                        idx: number = (reelId * 4) + 3;

                    if (visitedIdx.indexOf(idx) === -1) {
                        visitedIdx.push(idx);
                        specialReelTl.add(sym.playIgniteOverlayAnimation(reelId).timeScale(initTs + (betWayIdx * 0.2)), 0); // #308 => formula for ts

                        // #411
                        if (this._canPlayWinSounds && !specialRowSoundPlayed) {
                            specialRowSoundPlayed = true;
                            specialReelTl.add(() => {
                                KennethMustDie.soundPlayer.playSound(KMDSound.WIN_SET_SYMBOL_ON_FIRE);
                            }, 0);
                        }
                    }
                }
            }
            timeLine.add(specialReelTl);

            const reelTl: gsap.core.Timeline = gsap.timeline();
            for (let reelId: number = 0; reelId < betWayWin.reelWinPositions.length; reelId++) {
                const symTl: gsap.core.Timeline = gsap.timeline();
                let perReelSoundPlayed: boolean = false;

                for (let symId: number = 0; symId < betWayWin.reelWinPositions[reelId].length - 1; symId++) {
                    if (betWayWin.reelWinPositions[reelId][symId]) {
                        const effectiveSymId: number = (reelId > 0 && reelId < 4 ? symId : (symId - 1)),
                            symbol: SimpleReelModelSymbolData = KennethMustDie.reelView.reelModels[reelId].getStopSymbolAt(effectiveSymId),
                            sym: KMDSymbol = symbol.positionable as KMDSymbol,
                            idx: number = (reelId * 4) + symId;

                        if (visitedIdx.indexOf(idx) === -1) {
                            visitedIdx.push(idx);
                            symTl.add(sym.playIgniteOverlayAnimation(reelId).timeScale(initTs + (betWayIdx * 0.2) + (reelId * 0.05)), 0); // #308 => formula for ts

                            // #411
                            if (this._canPlayWinSounds && !perReelSoundPlayed) {
                                perReelSoundPlayed = true;
                                symTl.add(() => {
                                    KennethMustDie.soundPlayer.playSound(KMDSound.WIN_SET_SYMBOL_ON_FIRE);
                                }, 0);
                            }
                        }
                    }
                }
                reelTl.add(symTl);
            }
            timeLine.add(reelTl);
        });

        tl.add(timeLine, "doIgnition");
    }

    private explodeForHigherWins(allWinPositions: boolean[][], tl: gsap.core.Timeline): void {
        const timeLine: gsap.core.Timeline = gsap.timeline(),
            soundPlayedReelIds: boolean[] = [false, false, false, false, false];

        allWinPositions.forEach((value: boolean[], reelId: number) => {
            const reelTl: gsap.core.Timeline[] = [];
            value.forEach((val: boolean, symId: number) => {
                if (val) {
                    const symbolAnim: gsap.core.Timeline = gsap.timeline(),
                        reel: SimpleReelModel = (symId === 3 && reelId > 0 && reelId < 4) ? KennethMustDie.specialReelView.reel : KennethMustDie.reelView.reelModels[reelId],
                        effectiveSymId: number = (reelId > 0 && reelId < 4 ? (reel.id === 5 ? (reelId - 1) : symId) : (symId - 1)),
                        symbol: SimpleReelModelSymbolData = reel.getStopSymbolAt(effectiveSymId),
                        sym: KMDSymbol = symbol.positionable as KMDSymbol,
                        multi: number = KennethMustDie.model.gameData.multiplier[reelId][symId];

                    if (!KennethMustDie.model.reSpawnPositions[reelId][symId]) {
                        symbolAnim.add([
                            sym.playOverlayAnimation("symExplode"),
                            sym.displayMultiOnWin(multi)
                        ]);

                        // #236
                        if (this._canPlayWinSounds && !soundPlayedReelIds[reelId]) {
                            soundPlayedReelIds[reelId] = true;
                            symbolAnim.add(() => {
                                KennethMustDie.soundPlayer.playSound((this._lastReelForSound === reelId) ? KMDSound.SYM_EXPLODE_2 : KMDSound.SYM_EXPLODE_1);
                            }, (11 / 30));
                        }

                        symbolAnim.add(() => {
                            reel.removeAvalancheSymbol(symbol);

                            KennethMustDie.winOverlays.stopIgniteAnimation(reelId);

                            if (sym.getExtrasLength()) {
                                KennethMustDie.xWaysReveal.hideItem(reelId);
                            }

                            // #245
                            if (reel.id === KennethMustDie.REELS_NUM) {
                                KennethMustDie.multipliersChain.removeMultiplier(effectiveSymId);
                            }
                        }, "-=0.5");
                    } else {
                        symbolAnim.add(() => {
                            KennethMustDie.winOverlays.stopIgniteAnimation(reelId);
                        });
                    }

                    reelTl.push(symbolAnim);
                }
            });
            timeLine.add(reelTl, (0.2 * reelId));
        });

        tl.add(timeLine);
    }

    private turnOffIgnitionForBigWins(allWinPositions: boolean[][], tl: gsap.core.Timeline): void {
        const timeLine: gsap.core.Timeline = gsap.timeline();

        timeLine.to(this, {
            duration: 0.2
        });
        timeLine.add(() => {
            allWinPositions.forEach((value: boolean[], reelId: number) => {
                value.forEach((val: boolean) => {
                    if (val) {
                        KennethMustDie.winOverlays.stopIgniteAnimation(reelId);
                    }
                });
            });
        });

        tl.add(timeLine);
    }

    private doCollapseAction(tl: gsap.core.Timeline, allWinPositions: boolean[][]): void {
        tl.add(() => {
            allWinPositions.forEach((value: boolean[], reelId: number) => {
                value.forEach((val: boolean, symId: number) => {
                    if (!val) {
                        const reel: SimpleReelModel = (symId === 3 && reelId > 0 && reelId < 4) ? KennethMustDie.specialReelView.reel : KennethMustDie.reelView.reelModels[reelId],
                            effectiveSymId: number = (reelId > 0 && reelId < 4 ? (reel.id === 5 ? (reelId - 1) : symId) : (symId - 1)),
                            symbol: SimpleReelModelSymbolData = reel.getStopSymbolAt(effectiveSymId);

                        if (symbol) {
                            (symbol.positionable as KMDSymbol).removeNoWinTint();
                        }
                    }
                });
            });

            // #335
            KennethMustDie.xWaysReveal.hideAll();

            const nextMode: string = KennethMustDie.model.data.nextMode;
            let canPlaySound: boolean = false; // if anything gets sticky play this sound once only

            if (nextMode === KMDGameMode.FREESPIN_COOL_AVALANCHE || nextMode === KMDGameMode.FREESPIN_SUPER_AVALANCHE) {
                canPlaySound = true;
            } else if (nextMode === KMDGameMode.FREESPIN_AVALANCHE) {
                // middle 3 reels only
                for (let reelId: number = 1; reelId < 4; reelId++) {
                    const stopSymbols: SimpleReelModelSymbolData[] = KennethMustDie.reelView.reelModels[reelId].stopSymbols;

                    for (let symId: number = 0; symId < stopSymbols.length; symId++) {
                        if (!stopSymbols[symId].isRemoved && (stopSymbols[symId].positionable as KMDSymbol).originalName === SPECIAL_SYMBOL_NAMES.W) {
                            canPlaySound = true;

                            break;
                        }
                    }

                    if (canPlaySound) {
                        break;
                    }
                }
            }

            // #445, #479
            if (canPlaySound) {
                KennethMustDie.soundPlayer.playSound(KMDSound.SYM_CHAIN_ADD);
            }

            // #460
            KennethMustDie.canPlayCollapseSoundInMainReels = false;

            KennethMustDie.reelView.reelModels.forEach((reel: SimpleReelModel) => {
                reel.collapse();
            });

            // #460
            if (KennethMustDie.canPlayCollapseSoundInMainReels) {
                KennethMustDie.soundPlayer.playSound(KMDSound.AVALANCHE_SYMBOL_DROP_IN);
            }

            KennethMustDie.specialReelView.reel.collapse();
            KennethMustDie.multipliersChain.collapse();
        });
        tl.to(this, {
            duration: 0.15
        });
    }
}